import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Alert, CircularProgress, Avatar, Switch,
  FormControlLabel, Divider, Tooltip, IconButton,
} from '@mui/material';
import { Policy as PolicyIcon, Add, Edit, Delete, CheckCircle, Cancel } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

interface LeaveType {
  id: string; name: string; code: string; isPaid: boolean;
  requiresApproval: boolean; impactsPayroll: boolean;
  description?: string; isActive: boolean;
}

interface LeavePolicy {
  id: string; entityId: string; leaveTypeId: string;
  entity?: { name: string }; leaveType?: { name: string; code: string };
  annualEntitlementDays: number; accrualMethod: string;
  carryOverLimit?: number; allowNegativeBalance: boolean;
  maxConsecutiveDays?: number; noticePeriodDays?: number;
  minimumServiceMonths?: number; isActive: boolean;
}

const ACCRUAL_METHODS = [
  { value: 'upfront',        label: 'Upfront (full entitlement at start of year)' },
  { value: 'monthly',        label: 'Monthly (entitlement divided across 12 months)' },
  { value: 'per_pay_period', label: 'Per Pay Period' },
];

const emptyType = {
  name: '', code: '', isPaid: true, requiresApproval: true,
  impactsPayroll: true, description: '',
};

const emptyPolicy = {
  entityId: '', leaveTypeId: '', annualEntitlementDays: 14,
  accrualMethod: 'upfront', carryOverLimit: '', allowNegativeBalance: false,
  maxConsecutiveDays: '', noticePeriodDays: '', minimumServiceMonths: '',
};

export default function LeavePolicyAdmin() {
  const { user } = useAuth();
  const isHRAdmin = (user as any)?.role === 'hr_admin';
  const isHR = ['hr_admin', 'hr_manager'].includes((user as any)?.role || '');

  const [tab, setTab] = useState(0);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [policies, setPolicies] = useState<LeavePolicy[]>([]);
  const [entities, setEntities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Leave Type form
  const [typeFormOpen, setTypeFormOpen] = useState(false);
  const [typeForm, setTypeForm] = useState({ ...emptyType });
  const [editTypeId, setEditTypeId] = useState<string | null>(null);
  const [typeSaving, setTypeSaving] = useState(false);

  // Policy form
  const [policyFormOpen, setPolicyFormOpen] = useState(false);
  const [policyForm, setPolicyForm] = useState({ ...emptyPolicy });
  const [editPolicyId, setEditPolicyId] = useState<string | null>(null);
  const [policySaving, setPolicySaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [typesData, policiesData, entitiesData] = await Promise.all([
        apiFetch('/api/leave/types?includeInactive=true'),
        apiFetch('/api/leave/policies'),
        apiFetch('/api/entities'),
      ]);
      setLeaveTypes(Array.isArray(typesData) ? typesData : []);
      setPolicies(Array.isArray(policiesData) ? policiesData : []);
      setEntities(Array.isArray(entitiesData) ? entitiesData : []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Leave Types ──────────────────────────────────────────────────────
  const openCreateType = () => { setEditTypeId(null); setTypeForm({ ...emptyType }); setTypeFormOpen(true); };
  const openEditType = (t: LeaveType) => {
    setEditTypeId(t.id);
    setTypeForm({ name: t.name, code: t.code, isPaid: t.isPaid, requiresApproval: t.requiresApproval, impactsPayroll: t.impactsPayroll, description: t.description || '' });
    setTypeFormOpen(true);
  };

  const saveType = async () => {
    setTypeSaving(true);
    try {
      if (editTypeId) {
        await apiFetch(`/api/leave/types/${editTypeId}`, { method: 'PATCH', body: JSON.stringify(typeForm) });
      } else {
        await apiFetch('/api/leave/types', { method: 'POST', body: JSON.stringify(typeForm) });
      }
      setTypeFormOpen(false); load();
    } catch (e: any) { setError(e.message); }
    finally { setTypeSaving(false); }
  };

  const deactivateType = async (id: string) => {
    await apiFetch(`/api/leave/types/${id}`, { method: 'DELETE' });
    load();
  };

  // ── Policies ─────────────────────────────────────────────────────────
  const openCreatePolicy = () => { setEditPolicyId(null); setPolicyForm({ ...emptyPolicy }); setPolicyFormOpen(true); };
  const openEditPolicy = (p: LeavePolicy) => {
    setEditPolicyId(p.id);
    setPolicyForm({
      entityId: p.entityId, leaveTypeId: p.leaveTypeId,
      annualEntitlementDays: p.annualEntitlementDays,
      accrualMethod: p.accrualMethod,
      carryOverLimit: p.carryOverLimit?.toString() || '',
      allowNegativeBalance: p.allowNegativeBalance,
      maxConsecutiveDays: p.maxConsecutiveDays?.toString() || '',
      noticePeriodDays: p.noticePeriodDays?.toString() || '',
      minimumServiceMonths: p.minimumServiceMonths?.toString() || '',
    });
    setPolicyFormOpen(true);
  };

  const savePolicy = async () => {
    setPolicySaving(true);
    try {
      const payload = {
        ...policyForm,
        annualEntitlementDays: Number(policyForm.annualEntitlementDays),
        carryOverLimit: policyForm.carryOverLimit ? Number(policyForm.carryOverLimit) : null,
        maxConsecutiveDays: policyForm.maxConsecutiveDays ? Number(policyForm.maxConsecutiveDays) : null,
        noticePeriodDays: policyForm.noticePeriodDays ? Number(policyForm.noticePeriodDays) : null,
        minimumServiceMonths: policyForm.minimumServiceMonths ? Number(policyForm.minimumServiceMonths) : null,
      };
      if (editPolicyId) {
        await apiFetch(`/api/leave/policies/${editPolicyId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        await apiFetch('/api/leave/policies', { method: 'POST', body: JSON.stringify(payload) });
      }
      setPolicyFormOpen(false); load();
    } catch (e: any) { setError(e.message); }
    finally { setPolicySaving(false); }
  };

  const deactivatePolicy = async (id: string) => {
    await apiFetch(`/api/leave/policies/${id}`, { method: 'DELETE' });
    load();
  };

  const activeTypes = leaveTypes.filter(t => t.isActive);
  const inactiveTypes = leaveTypes.filter(t => !t.isActive);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#1565c0', width: 48, height: 48 }}><PolicyIcon /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Leave Policy Administration</Typography>
            <Typography variant="body2" color="text.secondary">Manage leave types, entitlements and accrual policies</Typography>
          </Box>
        </Box>
        {isHR && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {tab === 0 && isHRAdmin && (
              <Button variant="contained" startIcon={<Add />} onClick={openCreateType}
                sx={{ bgcolor: '#1565c0' }}>New Leave Type</Button>
            )}
            {tab === 1 && isHR && (
              <Button variant="contained" startIcon={<Add />} onClick={openCreatePolicy}
                sx={{ bgcolor: '#1565c0' }}>New Policy</Button>
            )}
          </Box>
        )}
      </Box>

      {!isHR && <Alert severity="info" sx={{ mb: 2 }}>Read-only view. Contact HR Admin to make changes.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Summary chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label={`${activeTypes.length} Active Leave Types`} color="primary" variant="outlined" />
        <Chip label={`${policies.filter(p => p.isActive).length} Active Policies`} color="success" variant="outlined" />
        {inactiveTypes.length > 0 && <Chip label={`${inactiveTypes.length} Inactive`} variant="outlined" sx={{ color: '#78909c' }} />}
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Leave Types (${leaveTypes.length})`} />
        <Tab label={`Entitlement Policies (${policies.length})`} />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* ── Leave Types ── */}
          {tab === 0 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell align="center">Paid</TableCell>
                    <TableCell align="center">Needs Approval</TableCell>
                    <TableCell align="center">Impacts Payroll</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    {isHRAdmin && <TableCell align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaveTypes.length === 0 && (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No leave types configured. Add your first one.
                    </TableCell></TableRow>
                  )}
                  {leaveTypes.map(t => (
                    <TableRow key={t.id} hover sx={{ opacity: t.isActive ? 1 : 0.5 }}>
                      <TableCell><Typography variant="body2" fontWeight={600}>{t.name}</Typography></TableCell>
                      <TableCell><Chip size="small" label={t.code} variant="outlined" sx={{ fontFamily: 'monospace' }} /></TableCell>
                      <TableCell align="center">
                        {t.isPaid ? <CheckCircle color="success" fontSize="small" /> : <Cancel sx={{ color: '#ccc' }} fontSize="small" />}
                      </TableCell>
                      <TableCell align="center">
                        {t.requiresApproval ? <CheckCircle color="success" fontSize="small" /> : <Cancel sx={{ color: '#ccc' }} fontSize="small" />}
                      </TableCell>
                      <TableCell align="center">
                        {t.impactsPayroll ? <CheckCircle color="success" fontSize="small" /> : <Cancel sx={{ color: '#ccc' }} fontSize="small" />}
                      </TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{t.description || '—'}</Typography></TableCell>
                      <TableCell>
                        <Chip size="small" label={t.isActive ? 'Active' : 'Inactive'}
                          color={t.isActive ? 'success' : 'default'} variant={t.isActive ? 'filled' : 'outlined'} />
                      </TableCell>
                      {isHRAdmin && (
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditType(t)}><Edit fontSize="small" /></IconButton></Tooltip>
                            {t.isActive && (
                              <Tooltip title="Deactivate">
                                <IconButton size="small" color="error" onClick={() => deactivateType(t.id)}><Delete fontSize="small" /></IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Policies ── */}
          {tab === 1 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Leave Type</TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell align="right">Days / Year</TableCell>
                    <TableCell>Accrual</TableCell>
                    <TableCell align="right">Max Carry-over</TableCell>
                    <TableCell align="center">Neg. Balance</TableCell>
                    <TableCell align="right">Notice (days)</TableCell>
                    <TableCell align="right">Min Service (mo.)</TableCell>
                    <TableCell>Status</TableCell>
                    {isHR && <TableCell align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {policies.length === 0 && (
                    <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No policies configured yet.
                    </TableCell></TableRow>
                  )}
                  {policies.map(p => (
                    <TableRow key={p.id} hover sx={{ opacity: p.isActive ? 1 : 0.5 }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{p.leaveType?.name ?? '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.leaveType?.code}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="caption">{p.entity?.name ?? '—'}</Typography></TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={700} color="#1565c0">{p.annualEntitlementDays}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={p.accrualMethod.replace('_', ' ')} variant="outlined"
                          sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }} />
                      </TableCell>
                      <TableCell align="right">{p.carryOverLimit ?? '—'}</TableCell>
                      <TableCell align="center">
                        {p.allowNegativeBalance
                          ? <CheckCircle color="warning" fontSize="small" />
                          : <Cancel sx={{ color: '#ccc' }} fontSize="small" />}
                      </TableCell>
                      <TableCell align="right">{p.noticePeriodDays ?? '—'}</TableCell>
                      <TableCell align="right">{p.minimumServiceMonths ?? '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={p.isActive ? 'Active' : 'Inactive'}
                          color={p.isActive ? 'success' : 'default'} variant={p.isActive ? 'filled' : 'outlined'} />
                      </TableCell>
                      {isHR && (
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditPolicy(p)}><Edit fontSize="small" /></IconButton></Tooltip>
                            {p.isActive && (
                              <Tooltip title="Deactivate">
                                <IconButton size="small" color="error" onClick={() => deactivatePolicy(p.id)}><Delete fontSize="small" /></IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Leave Type Form */}
      <Dialog open={typeFormOpen} onClose={() => setTypeFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editTypeId ? 'Edit Leave Type' : 'New Leave Type'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Name" value={typeForm.name}
              onChange={e => setTypeForm(p => ({ ...p, name: e.target.value }))} required
              placeholder="e.g. Annual Leave, Sick Leave, Paternity Leave" />
            <TextField sx={{ minWidth: 120 }} label="Code" value={typeForm.code}
              onChange={e => setTypeForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} required
              placeholder="e.g. AL, SL" inputProps={{ maxLength: 10 }} />
          </Box>
          <TextField fullWidth label="Description" value={typeForm.description}
            onChange={e => setTypeForm(p => ({ ...p, description: e.target.value }))}
            placeholder="Brief description of this leave type" />
          <Divider><Typography variant="caption" color="text.secondary">Rules</Typography></Divider>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel control={<Switch checked={typeForm.isPaid} onChange={e => setTypeForm(p => ({ ...p, isPaid: e.target.checked }))} />} label="Paid Leave" />
            <FormControlLabel control={<Switch checked={typeForm.requiresApproval} onChange={e => setTypeForm(p => ({ ...p, requiresApproval: e.target.checked }))} />} label="Requires Approval" />
            <FormControlLabel control={<Switch checked={typeForm.impactsPayroll} onChange={e => setTypeForm(p => ({ ...p, impactsPayroll: e.target.checked }))} />} label="Impacts Payroll" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTypeFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveType} disabled={typeSaving || !typeForm.name || !typeForm.code}
            sx={{ bgcolor: '#1565c0' }}>
            {typeSaving ? 'Saving…' : editTypeId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Policy Form */}
      <Dialog open={policyFormOpen} onClose={() => setPolicyFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editPolicyId ? 'Edit Leave Policy' : 'New Leave Policy'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Alert severity="info">
            Policies define entitlements for a specific leave type per company entity.
          </Alert>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select fullWidth label="Leave Type" value={policyForm.leaveTypeId}
              onChange={e => setPolicyForm(p => ({ ...p, leaveTypeId: e.target.value }))} required disabled={!!editPolicyId}>
              {activeTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Entity" value={policyForm.entityId}
              onChange={e => setPolicyForm(p => ({ ...p, entityId: e.target.value }))} required disabled={!!editPolicyId}>
              {entities.map((e: any) => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Annual Entitlement (days)" type="number"
              value={policyForm.annualEntitlementDays}
              onChange={e => setPolicyForm(p => ({ ...p, annualEntitlementDays: Number(e.target.value) }))}
              required inputProps={{ min: 0, step: 0.5 }} />
            <TextField select fullWidth label="Accrual Method" value={policyForm.accrualMethod}
              onChange={e => setPolicyForm(p => ({ ...p, accrualMethod: e.target.value }))}>
              {ACCRUAL_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Max Carry-over (days)" type="number"
              value={policyForm.carryOverLimit}
              onChange={e => setPolicyForm(p => ({ ...p, carryOverLimit: e.target.value }))}
              helperText="Leave blank for no carry-over" inputProps={{ min: 0 }} />
            <TextField fullWidth label="Max Consecutive Days" type="number"
              value={policyForm.maxConsecutiveDays}
              onChange={e => setPolicyForm(p => ({ ...p, maxConsecutiveDays: e.target.value }))}
              helperText="Leave blank for no limit" inputProps={{ min: 1 }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Advance Notice Required (days)" type="number"
              value={policyForm.noticePeriodDays}
              onChange={e => setPolicyForm(p => ({ ...p, noticePeriodDays: e.target.value }))}
              inputProps={{ min: 0 }} />
            <TextField fullWidth label="Minimum Service (months)" type="number"
              value={policyForm.minimumServiceMonths}
              onChange={e => setPolicyForm(p => ({ ...p, minimumServiceMonths: e.target.value }))}
              helperText="e.g. 6 for half-year eligible" inputProps={{ min: 0 }} />
          </Box>
          <FormControlLabel
            control={<Switch checked={policyForm.allowNegativeBalance}
              onChange={e => setPolicyForm(p => ({ ...p, allowNegativeBalance: e.target.checked }))} />}
            label="Allow negative balance (advance leave)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPolicyFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePolicy}
            disabled={policySaving || !policyForm.leaveTypeId || !policyForm.entityId || !policyForm.annualEntitlementDays}
            sx={{ bgcolor: '#1565c0' }}>
            {policySaving ? 'Saving…' : editPolicyId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
